import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { maintenanceAPI, assetsAPI, usersAPI } from '../../api';
import toast from 'react-hot-toast';
import styles from './Maintenance.module.css';

const FREQ_COLORS = { daily:{bg:'#fee2e2',color:'#b91c1c'}, weekly:{bg:'#fff7ed',color:'#c2410c'}, monthly:{bg:'#eff4ff',color:'#1a56db'}, quarterly:{bg:'#f0fdf4',color:'#16a34a'}, half_yearly:{bg:'#fdf4ff',color:'#9333ea'}, yearly:{bg:'#f1f5f9',color:'#64748b'} };

function NewPMModal({ assets, users, onClose, onSave }) {
  const [form, setForm] = useState({ asset_id:'', title:'', frequency_type:'monthly', frequency_value:1, assigned_to:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = async () => {
    if(!form.asset_id||!form.title){toast.error('Asset and title required');return;}
    setSaving(true);
    try { await maintenanceAPI.create(form); toast.success('PM scheduled'); onSave(); }
    catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={styles.modal} style={{width:500}} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2>Schedule PM</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div className={styles.form_grid}>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Asset *</label><select value={form.asset_id} onChange={e=>set('asset_id',e.target.value)}><option value="">Select asset</option>{assets.map(a=><option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}</select></div>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Task Title *</label><input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="e.g. Monthly Lubrication Check"/></div>
            <div className={styles.form_group}><label>Frequency</label><select value={form.frequency_type} onChange={e=>set('frequency_type',e.target.value)}>{['daily','weekly','monthly','quarterly','half_yearly','yearly'].map(v=><option key={v} value={v}>{v.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}</select></div>
            <div className={styles.form_group}><label>Assign To</label><select value={form.assigned_to} onChange={e=>set('assigned_to',e.target.value)}><option value="">Unassigned</option>{users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
          </div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Schedule PM'}</button>
        </div>
      </div>
    </div>
  );
}

function DetailPanel({ item, type }) {
  const navigate = useNavigate();
  if (!item) return <div className={styles.detail_empty}>Select a schedule to view details</div>;
  const isOverdue = item.next_due && new Date(item.next_due) < new Date();
  const fc = FREQ_COLORS[item.frequency_type]||{bg:'#f1f5f9',color:'#64748b'};

  if (type === 'overdue') {
    const days = Math.floor((Date.now() - new Date(item.due_date)) / 86400000);
    return (
      <div className={styles.detail_panel}>
        <div className={styles.dp_header}>
          <span className={styles.dp_wo_num}>{item.wo_number}</span>
          <span style={{background:'#fee2e2',color:'#b91c1c',fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:12}}>⚠ {days}d overdue</span>
        </div>
        <div className={styles.dp_title}>{item.asset_name}</div>
        <div className={styles.dp_desc} style={{color:'#ef4444',fontWeight:600}}>PM Work Order is {days} days overdue</div>
        <div className={styles.dp_actions}>
          <button className={styles.dp_btn_primary} onClick={()=>navigate(`/work-orders/${item.id}`)}>View Work Order →</button>
        </div>
        <div className={styles.dp_divider}/>
        <div className={styles.dp_section}>Details</div>
        {[['Asset',item.asset_name],['Asset Code',item.asset_code],['Location',item.location_name],['Due Date',item.due_date?.slice(0,10)],['Status',item.status]].filter(([,v])=>v).map(([l,v])=>(
          <div key={l} className={styles.dp_row}><span>{l}</span><strong>{v}</strong></div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.detail_panel}>
      <div className={styles.dp_header}>
        <span style={{background:fc.bg,color:fc.color,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:12}}>{item.frequency_type?.replace(/_/g,' ')}</span>
        {isOverdue && <span style={{background:'#fee2e2',color:'#b91c1c',fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:12}}>Overdue</span>}
      </div>
      <div className={styles.dp_title}>{item.title}</div>
      <div className={styles.dp_actions}>
        <button className={styles.dp_btn_primary} onClick={()=>navigate(`/assets/${item.asset_id}/profile`)}>View Asset →</button>
      </div>
      <div className={styles.dp_divider}/>
      <div className={styles.dp_section}>Schedule Details</div>
      {[['Asset',item.asset_name],['Asset Code',item.asset_code],['Frequency',item.frequency_type?.replace(/_/g,' ')],['Assigned To',item.assigned_name],['Last Done',item.last_done?.slice(0,10)||'Never'],['Next Due',item.next_due?.slice(0,10)]].filter(([,v])=>v).map(([l,v])=>(
        <div key={l} className={styles.dp_row}><span>{l}</span><strong style={l==='Next Due'&&isOverdue?{color:'#ef4444',fontWeight:700}:{}}>{v}</strong></div>
      ))}
    </div>
  );
}

export default function MaintenancePage() {
  const navigate  = useNavigate();
  const [pm,      setPm]      = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assets,  setAssets]  = useState([]);
  const [users,   setUsers]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [selType,  setSelType]  = useState('pm');
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState('pm'); // 'pm' | 'overdue'
  const [search, setSearch]   = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([maintenanceAPI.list(), maintenanceAPI.overdue()])
      .then(([p,o])=>{ setPm(p.data.data); setOverdue(o.data.data); })
      .finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); assetsAPI.list({limit:200}).then(r=>setAssets(r.data.data)).catch(()=>{}); usersAPI.list().then(r=>setUsers(r.data.data)).catch(()=>{}); },[]);

  const displayList = (activeTab==='pm' ? pm : overdue).filter(i => !search || (i.asset_name||'').toLowerCase().includes(search.toLowerCase()) || (i.title||'').toLowerCase().includes(search.toLowerCase()) || (i.wo_number||'').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button className={styles.new_btn_main} style={{borderRadius:6}} onClick={() => navigate('/maintenance/new?type=pm')}>🔧 ＋ Schedule PM</button>
        <div className={styles.search_wrap}>
          <span className={styles.search_icon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className={styles.search_input} placeholder="Search schedules…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className={styles.toolbar_right}><span className={styles.total_badge}>{pm.length} schedules · {overdue.length} overdue</span></div>
      </div>

      <div className={styles.body}>
        {/* Left sidebar - tabs */}
        <div className={styles.filter_col}>
          <div className={`${styles.filter_item} ${activeTab==='pm'?styles.filter_active:''}`} onClick={()=>setActiveTab('pm')}>
            <span className={styles.filter_dot} style={{background:'#1a56db'}}/>
            <span className={styles.filter_label}>PM Schedules</span>
            <span className={`${styles.filter_count} ${activeTab==='pm'?styles.filter_count_active:''}`}>{pm.length}</span>
          </div>
          <div className={`${styles.filter_item} ${activeTab==='overdue'?styles.filter_active:''}`} onClick={()=>setActiveTab('overdue')}>
            <span className={styles.filter_dot} style={{background:'#ef4444'}}/>
            <span className={styles.filter_label}>Overdue PMs</span>
            <span className={`${styles.filter_count} ${activeTab==='overdue'?styles.filter_count_active:''}`} style={overdue.length>0?{background:'#ef4444',color:'#fff'}:{}}>{overdue.length}</span>
          </div>
          <div className={styles.filter_divider}/>
          <div className={styles.filter_section}>By Frequency</div>
          {Object.entries(FREQ_COLORS).map(([freq,col])=>{
            const cnt=pm.filter(p=>p.frequency_type===freq).length;
            return cnt>0&&(
            <div key={freq} className={styles.filter_item}>
              <span className={styles.filter_dot} style={{background:col.color}}/>
              <span className={styles.filter_label}>{freq.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</span>
              <span className={styles.filter_count}>{cnt}</span>
            </div>);
          })}
        </div>

        {/* Center list */}
        <div className={styles.list_col}>
          {loading?<div className={styles.loading}>Loading…</div>
          :displayList.length===0?<div className={styles.empty}><div style={{fontSize:40,marginBottom:12}}>{activeTab==='overdue'?'🎉':'📅'}</div><div style={{fontWeight:600,color:'#1e2a3b',marginBottom:6}}>{activeTab==='overdue'?'No overdue PMs!':'No PM schedules'}</div></div>
          :displayList.map((item,i)=>{
            const isSel=selected?.id===item.id&&selType===activeTab;
            if(activeTab==='overdue'){
              const days=Math.floor((Date.now()-new Date(item.due_date))/86400000);
              return(
              <div key={i} className={`${styles.card} ${isSel?styles.card_active:''} ${styles.card_overdue}`} onClick={()=>{setSelected(item);setSelType('overdue');}}>
                <div className={styles.card_stripe} style={{background:'#ef4444'}}/>
                <div className={styles.card_body}>
                  <div className={styles.card_top}><span className={styles.card_wo_num}>{item.wo_number}</span><span style={{background:'#fee2e2',color:'#b91c1c',fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:10}}>⚠ {days}d overdue</span></div>
                  <div className={styles.card_title}>{item.asset_name}</div>
                  <div className={styles.card_meta}>{item.asset_code} · {item.location_name}</div>
                  <div className={styles.card_footer}><span style={{fontSize:12,color:'#ef4444',fontWeight:600}}>Due: {item.due_date?.slice(0,10)}</span></div>
                </div>
              </div>);
            }
            const isOverdue=item.next_due&&new Date(item.next_due)<new Date();
            const fc=FREQ_COLORS[item.frequency_type]||{bg:'#f1f5f9',color:'#64748b'};
            return(
            <div key={i} className={`${styles.card} ${isSel?styles.card_active:''}`} onClick={()=>{setSelected(item);setSelType('pm');}}>
              <div className={styles.card_stripe} style={{background:fc.color}}/>
              <div className={styles.card_body}>
                <div className={styles.card_top}>
                  <span style={{background:fc.bg,color:fc.color,fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:10}}>{item.frequency_type?.replace(/_/g,' ')}</span>
                  {isOverdue&&<span style={{background:'#fee2e2',color:'#b91c1c',fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:10}}>Overdue</span>}
                </div>
                <div className={styles.card_title}>{item.title}</div>
                <div className={styles.card_meta}>{item.asset_name} · {item.asset_code}</div>
                <div className={styles.card_footer}>
                  <span style={{fontSize:12,color:'#64748b'}}>{item.assigned_name||'Unassigned'}</span>
                  <span style={{fontSize:12,color:isOverdue?'#ef4444':'#94a3b8',fontWeight:isOverdue?600:400}}>Next: {item.next_due?.slice(0,10)||'—'}</span>
                </div>
              </div>
            </div>);
          })}
        </div>

        {/* Right detail */}
        <div className={styles.detail_col}><DetailPanel item={selected} type={selType}/></div>
      </div>

    </div>
  );
}
